/**
 * Manages checking status of customers requests pending for confirmation
 */
import Queue from "@segment/localstorage-retry";
import {
  CustomerRequest,
  CustomerResponsePending,
  CustomerResponseError
} from "../types";
import debug from "./debug";
import api from "./api";
import { addErrorNotification, addSuccessNotification } from "./notifications";

const DROP_AFTER_DAYS = 30;
const DROP_AFTER_TIME = DROP_AFTER_DAYS * 24 * 60 * 60 * 1000;
const HASHCODE_EXPIRED_CODE = 410;
const HASHCODE_NOT_FOUND_CODE = 404;
const HASHCODE_INVALID_CODE = 400;

export const REQUEST_ID_IS_RETRYING: { [propName: string]: number } = {};

const QUEUE_OPTIONS = {
  minRetryDelay: 60 * 1000, // min retry delay in ms (used in exp. backoff calcs)
  maxRetryDelay: 120 * 1000, // max retry delay in ms (used in exp. backoff calcs)
  backoffFactor: 2, // exponential backoff factor (attempts^n)
  backoffJitter: 0, // jitter factor for backoff calcs (0 is usually fine)
  maxItems: Infinity, // queue high water mark (we suggest 100 as a max)
  maxAttempts: Infinity
};

const queue = new Queue(
  "checkCreationStatus",
  QUEUE_OPTIONS,
  (
    pendingResponse: CustomerResponsePending,
    done: (
      customerResError: string | null,
      pendingResponse: CustomerResponsePending
    ) => void
  ) => {
    //debug("[queueStatus] Checking status for:", pendingResponse);
    api
      .getCustomerIdByHashcode(pendingResponse.hashcode)
      .then((identifier: string) => {
        //debug("[queueStatus] getCustomerIdByHashcode: res:", identifier);
        if (identifier) {
          pendingResponse.data.identifier = identifier;
          done(null, pendingResponse);
        } else {
          //debug("[queueStatus] failed, retry");
          return done("No Identifier", pendingResponse);
        }
      })
      .catch(err => {
        debug(
          "[queueStatus] getCustomerIdByHashcode: err:",
          err.message,
          err.code
        );
        // return done and retry
        return done(err, pendingResponse);
      });
  }
);

queue.shouldRetry = function(
  item: CustomerResponsePending,
  attemptNumber: number,
  error: any
) {
  debug("[queueStatus] shouldRetry: error:", error.code, error.message);
  const errorCode = error && error.code;
  if (
    errorCode === HASHCODE_EXPIRED_CODE ||
    errorCode === HASHCODE_INVALID_CODE ||
    errorCode === HASHCODE_NOT_FOUND_CODE
  ) {
    showErrorNotification(item, errorCode, error.message);
    return false;
  }
  // max attempts
  if (attemptNumber > this.maxAttempts) {
    showErrorNotification(item, 408, "No confimation from the customer");
    return false;
  }

  // too old
  const requestDate = new Date(item.requestDate);
  const timePassed = new Date().getTime() - requestDate.getTime();
  if (timePassed > DROP_AFTER_TIME) {
    showErrorNotification(item, 408, "No confimation from the customer");
    return false;
  }
  // retry
  return true;
};

/*
 * Runs after processing item, wether if succeeded or not
 */
queue.on("processed", (error: string, customerRes: CustomerResponsePending) => {
  if (error) {
    //debug("[queueStatus] client not found yet:", customerRes);
  } else {
    //debug("[queueStatus] processing completed", customerRes);
    addSuccessNotification(customerRes);
    api.fireGoogleAnalyticsSuccessEvent(customerRes);
  }
});

// If a queued item has exceeded maximum retries, it will be discarded
queue.on("discard", (item: CustomerResponsePending, attempts: number) => {
  debug("[queueStatus] discarded:", item, attempts);
});

function showErrorNotification(
  item: CustomerResponsePending,
  errorCode: number,
  errorMessage: string
) {
  const request: CustomerRequest = {
    ...item,
    type: "create"
  };
  const response: CustomerResponseError = {
    ...item,
    errorCode: errorCode,
    errorMessage:
      errorMessage +
      ` (${item.data.title} ${item.data.firstName} ${item.data.lastName})`
  };
  addErrorNotification(request, response);
}

// Only run if not in running tests environment
if (process.env.NODE_ENV !== "test") {
  queue.start();
}

export default queue;
